import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { PackageDetailShimmer } from '../components/shimmer';
import { useGetItemByIdQuery } from '../store/edosApi';
import { useListOffersQuery, useGetContactInfoQuery } from '../store/siteContentApi';
import { mapApiToPackageDetail } from '../utils/mapApiDetail';
import { resolvePricing } from '../utils/offerLookup';
import { useCart } from '../context/CartContext';
import { useUI } from '../context/UIContext';
import { ROUTES } from '../constants/routes';
import { UI, DISPLAY, EASE } from '../styles/tl5';
import CATALOGUE, { byName } from '../data/packageFamilies';

// Package detail — built to the v5 reference.
//
// Full-width dark hero, then a long left column beside a sticky booking
// card. The "What's included" accordion and the comparison table both run
// on the curated body-system grouping in src/data/packageFamilies.js,
// joined to this package by name — the eDOS row itself only carries one
// flat comma-separated parameter string.
//
// Two departures from the reference, both because of the data:
//   · It compares three named packages on seven hand-written feature
//     rows. There is no such list for 61 packages, so the table compares
//     this package against the others in its own family, on the body
//     systems they actually cover. Same shape, real content.
//   · It prints "₹ [price]". The packages endpoint returns mrp and
//     l2l_price as null on every row, so the card says "Price on
//     request" until pricing exists.

const FALLBACK_CONTACT = { phone: '1800 599 5656', whatsapp: '+919014638633' };

const sectionCard = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
  p: 'clamp(20px, 3.4vw, 32px)',
  borderRadius: '28px',
  bgcolor: '#FFFFFF',
};

const sectionTitle = {
  m: 0,
  fontFamily: DISPLAY,
  fontWeight: 700,
  fontSize: 'clamp(26px, 3vw, 36px)',
  lineHeight: 1.05,
  letterSpacing: '-.02em',
  color: 'var(--tl-g9)',
};

const pill = {
  p: '10px 14px',
  borderRadius: '999px',
  bgcolor: 'var(--tl-mint)',
  fontFamily: UI,
  fontSize: 14,
  fontWeight: 700,
  color: 'var(--tl-g9)',
};

const PackageDetail = () => {
  const { id } = useParams();
  const { toggleItem, inCart } = useCart();
  const { showToast } = useUI();
  // The reference opens the first group and leaves the rest shut.
  const [open, setOpen] = useState({ 0: true });
  const [people, setPeople] = useState(1);

  const { data, isLoading, isError, error } = useGetItemByIdQuery(id, { skip: !id });
  const raw = mapApiToPackageDetail(data);

  const { data: offers = [] } = useListOffersQuery();
  const pricing = raw ? resolvePricing(offers, raw.id, raw.price) : null;
  const pkg = raw && pricing
    ? { ...raw, price: pricing.finalPrice, original: pricing.originalPrice }
    : raw;

  const { data: contact } = useGetContactInfoQuery();

  const meta = pkg ? byName(pkg.name) : null;

  // Up to two siblings from the same family, for the comparison table.
  const family = useMemo(() => {
    if (!meta) return [];
    return CATALOGUE.filter((p) => p.f === meta.f);
  }, [meta]);

  const compare = useMemo(() => {
    if (!meta || family.length < 2) return null;
    const others = family.filter((p) => p.n !== meta.n).slice(0, 2);
    const columns = [meta, ...others];
    // Every body system any of the compared packages covers, in the
    // current package's order first so its own rows read top-down.
    const systems = [];
    for (const col of columns) {
      for (const g of col.sys) if (!systems.includes(g.s)) systems.push(g.s);
    }
    return {
      columns: columns.map((c) => ({ name: c.n, current: c.n === meta.n })),
      rows: systems.map((s) => ({
        name: s,
        cells: columns.map((c) => {
          const g = c.sys.find((x) => x.s === s);
          return g ? `${g.p.length}` : '–';
        }),
      })),
    };
  }, [meta, family]);

  if (isLoading) return <PackageDetailShimmer />;

  if (isError || !pkg || data?.notFound) {
    return (
      <Box sx={{ maxWidth: 1320, mx: 'auto', px: 3, py: 10, textAlign: 'center' }}>
        <Typography component="h1" sx={{ ...sectionTitle, fontSize: 32, mb: 1.5 }}>
          Package not found
        </Typography>
        <Typography sx={{ fontFamily: UI, color: 'var(--tl-muted)', mb: 3 }}>
          {error?.data?.message || 'The link may be out of date, or the package was renamed.'}
        </Typography>
        <Box
          component={Link}
          to={ROUTES.PACKAGES}
          sx={{
            display: 'inline-block', p: '14px 22px', borderRadius: '999px',
            bgcolor: 'var(--tl-g9)', color: '#fff', fontFamily: UI, fontWeight: 800,
            textDecoration: 'none',
          }}
        >
          Browse all packages
        </Box>
      </Box>
    );
  }

  const isInCart = inCart(pkg.id, 'package');
  const hasPrice = Number(pkg.price) > 0;
  const hasDiscount = Number(pkg.original) > Number(pkg.price);

  const phone = contact?.phone || FALLBACK_CONTACT.phone;
  const whatsapp = String(contact?.whatsapp || FALLBACK_CONTACT.whatsapp).replace(/\D/g, '');

  // Prefer the curated body-system grouping; fall back to the one flat
  // group mapApiToPackageDetail builds when a package is not in the map.
  const groups = meta?.sys?.length
    ? meta.sys.map((g) => ({ name: g.s, tests: g.p }))
    : (pkg.groups || []).map((g) => ({ name: g.name, tests: g.tests }));

  const valueCount = meta?.par || pkg.paramCount;
  const profileCount = groups.reduce((t, g) => t + g.tests.length, 0);

  const who = String(data?.diseases || '')
    .split(',').map((s) => s.trim()).filter((s) => s && s !== '—');

  const facts = [
    { l: 'Tests', v: `${profileCount} profiles, ${valueCount} values` },
    { l: 'Fasting', v: pkg.fasting ? '10–12 hours' : 'Not needed' },
    { l: 'Sample', v: data?.sample_type || 'Blood' },
    { l: 'Report', v: pkg.tat },
  ];

  const prep = [
    pkg.fasting
      ? { t: 'Fast for 10–12 hours', d: 'Dinner by 8–9 pm, then only water until your sample.' }
      : { t: 'No fasting needed', d: 'You can eat and drink normally before this visit.' },
    { t: 'Book an early slot', d: '6–8 am slots keep the fasting wait short.' },
    { t: 'One visit covers it', d: 'The Trustian brings every container this package needs.' },
  ];

  const handleCart = () => {
    toggleItem({
      id: pkg.id, type: 'package', name: pkg.name, price: pkg.price,
      original: pkg.original, icon: pkg.icon, tat: pkg.tat, paramCount: pkg.paramCount,
    });
    showToast(isInCart ? 'Removed from cart' : 'Added to cart');
  };

  const crumb = { color: 'var(--tl-muted-2)', textDecoration: 'none', '&:hover': { color: 'var(--tl-g9)' } };

  return (
    <Box>
      {/* ── breadcrumb ───────────────────────────────────────────────── */}
      <Box
        sx={{
          width: '100%', maxWidth: 1320, mx: 'auto', pt: 2.5, px: 3,
          display: 'flex', flexWrap: 'wrap', gap: 1,
          fontFamily: UI, fontSize: 14, fontWeight: 600, color: 'var(--tl-muted-2)',
        }}
      >
        <Box component={Link} to={ROUTES.HOME} sx={crumb}>Home</Box>
        <span>/</span>
        <Box component={Link} to={ROUTES.PACKAGES} sx={crumb}>Packages</Box>
        <span>/</span>
        <Box component="span" sx={{ color: 'var(--tl-g9)' }}>{pkg.name}</Box>
      </Box>

      {/* ── hero ─────────────────────────────────────────────────────── */}
      <Box component="section" sx={{ pt: 2, px: 2 }}>
        <Box
          sx={{
            width: '100%', maxWidth: 1320, mx: 'auto',
            display: 'flex', flexDirection: 'column', gap: 2.5,
            p: 'clamp(28px, 5vw, 56px)', borderRadius: '36px',
            bgcolor: 'var(--tl-g9)', color: '#FFFFFF',
          }}
        >
          {meta?.f && (
            <Box
              component="span"
              sx={{
                alignSelf: 'start', px: '10px', py: '6px', borderRadius: '999px',
                bgcolor: 'var(--tl-yellow)', color: 'var(--tl-g9)',
                fontFamily: UI, fontSize: 12, fontWeight: 800,
              }}
            >
              {meta.f}
            </Box>
          )}

          <Typography
            component="h1"
            sx={{
              m: 0, fontFamily: DISPLAY, fontWeight: 700,
              fontSize: 'clamp(32px, 5.4vw, 64px)', lineHeight: 0.95,
              letterSpacing: '-.035em', textWrap: 'balance', color: '#FFFFFF',
              overflowWrap: 'anywhere',
            }}
          >
            {pkg.name}
          </Typography>

          {(meta?.q || pkg.description) && (
            <Typography
              sx={{ m: 0, fontFamily: UI, fontSize: 18, lineHeight: 1.55, color: 'var(--tl-line-2)', maxWidth: 620 }}
            >
              {meta?.q || pkg.description}
            </Typography>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 140px), 1fr))',
              gap: 1, maxWidth: 760,
            }}
          >
            {facts.map((f) => (
              <Box
                key={f.l}
                sx={{
                  display: 'flex', flexDirection: 'column', gap: 0.5,
                  p: '14px 16px', borderRadius: '18px', bgcolor: 'var(--tl-g7)',
                }}
              >
                <Box component="span" sx={{ fontFamily: UI, fontSize: 12, fontWeight: 700, color: '#9FC2BA' }}>
                  {f.l}
                </Box>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 17, fontWeight: 800 }}>
                  {f.v}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        component="main"
        sx={{ width: '100%', maxWidth: 1320, mx: 'auto', px: 2, pt: 1.5, pb: 9 }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
          {/* ── left column ──────────────────────────────────────────── */}
          <Box sx={{ flex: '2 1 560px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {groups.length > 0 && (
              <Box component="section" sx={{ ...sectionCard, gap: 1 }}>
                <Box component="span" sx={{ ...sectionTitle, pb: 1 }}>What&rsquo;s included</Box>
                {groups.map((g, i) => {
                  const on = !!open[i];
                  return (
                    <Box key={g.name} sx={{ borderTop: '1px solid #EEF3F1' }}>
                      <Box
                        component="button"
                        type="button"
                        onClick={() => setOpen((o) => ({ ...o, [i]: !o[i] }))}
                        aria-expanded={on}
                        sx={{
                          width: '100%', display: 'flex', justifyContent: 'space-between',
                          alignItems: 'center', gap: 2, py: 2,
                          background: 'none', border: 0, cursor: 'pointer', textAlign: 'left',
                          font: 'inherit', color: 'var(--tl-g9)',
                        }}
                      >
                        <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                          <Box component="span" sx={{ fontFamily: UI, fontSize: 17, fontWeight: 800 }}>
                            {g.name}
                          </Box>
                        </Box>
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0 }}>
                          <Box component="span" sx={{ fontFamily: UI, fontSize: 13, fontWeight: 700, color: 'var(--tl-muted-2)' }}>
                            {g.tests.length} {g.tests.length === 1 ? 'test' : 'tests'}
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              display: 'grid', placeItems: 'center', width: 30, height: 30,
                              borderRadius: '50%', bgcolor: 'var(--tl-mint)',
                            }}
                          >
                            {on ? '−' : '+'}
                          </Box>
                        </Box>
                      </Box>
                      {on && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, pb: 2 }}>
                          {g.tests.map((x) => (
                            <Box
                              key={x}
                              component="span"
                              sx={{
                                p: '8px 12px', borderRadius: '999px', bgcolor: 'var(--tl-mint)',
                                fontFamily: UI, fontSize: 14, fontWeight: 700, color: 'var(--tl-g9)',
                              }}
                            >
                              {x}
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}

            {who.length > 0 && (
              <Box component="section" sx={sectionCard}>
                <Box component="span" sx={sectionTitle}>Who it&rsquo;s for</Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {who.map((w) => <Box key={w} component="span" sx={pill}>{w}</Box>)}
                </Box>
              </Box>
            )}

            <Box component="section" sx={{ ...sectionCard, bgcolor: 'var(--tl-yellow)', gap: 1.75 }}>
              <Box component="span" sx={sectionTitle}>How to prepare</Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 1 }}>
                {prep.map((p) => (
                  <Box
                    key={p.t}
                    sx={{
                      display: 'flex', flexDirection: 'column', gap: 0.75,
                      p: 2, borderRadius: '18px', bgcolor: 'rgba(255,255,255,0.7)',
                    }}
                  >
                    <Box component="span" sx={{ fontFamily: UI, fontSize: 16, fontWeight: 800, color: 'var(--tl-g9)' }}>
                      {p.t}
                    </Box>
                    <Box component="span" sx={{ fontFamily: UI, fontSize: 14, lineHeight: 1.5, color: '#3A4020' }}>
                      {p.d}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>

            {compare && (
              <Box component="section" sx={sectionCard}>
                <Box component="span" sx={sectionTitle}>Compare packages</Box>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 14, color: 'var(--tl-muted-2)' }}>
                  Profiles covered per body system, against the rest of the {meta.f} family.
                </Box>
                {/* Scrolls sideways rather than squeezing four columns onto
                    a phone, which is what the reference does too. */}
                <Box sx={{ overflowX: 'auto' }}>
                  <Box sx={{ minWidth: 560, display: 'flex', flexDirection: 'column' }}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: `1.4fr repeat(${compare.columns.length}, 1fr)`,
                        gap: 1, py: 1.25,
                        fontFamily: UI, fontSize: 14, fontWeight: 800,
                      }}
                    >
                      <Box component="span" />
                      {compare.columns.map((c) => (
                        <Box
                          key={c.name}
                          component="span"
                          sx={{
                            textAlign: 'center', p: '6px', borderRadius: '999px',
                            ...(c.current ? { bgcolor: 'var(--tl-g9)', color: '#FFFFFF' } : null),
                          }}
                        >
                          {c.name.replace(/^TrustLab\s+/, '')}
                        </Box>
                      ))}
                    </Box>
                    {compare.rows.map((r) => (
                      <Box
                        key={r.name}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: `1.4fr repeat(${compare.columns.length}, 1fr)`,
                          gap: 1, py: 1.5, alignItems: 'center',
                          borderTop: '1px solid #EEF3F1',
                          fontFamily: UI, fontSize: 15,
                        }}
                      >
                        <Box component="span" sx={{ fontWeight: 700 }}>{r.name}</Box>
                        {r.cells.map((cell, ci) => (
                          <Box
                            key={compare.columns[ci].name}
                            component="span"
                            sx={{
                              textAlign: 'center', fontWeight: 800,
                              color: cell === '–' ? '#B9C9C4' : 'var(--tl-teal-d)',
                            }}
                          >
                            {cell}
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>

          {/* ── right column ─────────────────────────────────────────── */}
          <Box
            component="aside"
            sx={{
              flex: '1 1 320px', minWidth: 0,
              display: 'flex', flexDirection: 'column', gap: 1.5,
              position: { md: 'sticky' },
              top: { md: 100 },
            }}
          >
            <Box
              component="section"
              sx={{ ...sectionCard, boxShadow: '0 20px 50px -30px rgba(11,43,39,0.4)' }}
            >
              {hasPrice ? (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1.5 }}>
                  <Box component="span" sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 36, letterSpacing: '-.02em' }}>
                    ₹ {Number(pkg.price).toLocaleString('en-IN')}
                  </Box>
                  {hasDiscount && (
                    <Box
                      component="span"
                      sx={{ fontFamily: UI, fontSize: 14, fontWeight: 700, color: 'var(--tl-muted-2)', textDecoration: 'line-through' }}
                    >
                      ₹ {Number(pkg.original).toLocaleString('en-IN')}
                    </Box>
                  )}
                </Box>
              ) : (
                <Box component="span" sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 26, color: 'var(--tl-muted-2)' }}>
                  Price on request
                </Box>
              )}

              <Box
                sx={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  gap: 1.5, p: '10px 12px', borderRadius: '16px', bgcolor: '#F6FAF8',
                }}
              >
                <Box component="span" sx={{ fontFamily: UI, fontSize: 15, fontWeight: 800 }}>People</Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {[
                    { t: '−', on: () => setPeople((p) => Math.max(1, p - 1)), dis: people <= 1, lbl: 'Fewer people' },
                    null,
                    { t: '+', on: () => setPeople((p) => Math.min(6, p + 1)), dis: people >= 6, lbl: 'More people' },
                  ].map((b, i) => (b ? (
                    <Box
                      key={b.t}
                      component="button"
                      type="button"
                      onClick={b.on}
                      disabled={b.dis}
                      aria-label={b.lbl}
                      sx={{
                        width: 36, height: 36, borderRadius: '50%', border: 0,
                        bgcolor: '#FFFFFF', color: 'var(--tl-g9)', cursor: 'pointer',
                        font: 'inherit', fontFamily: UI, fontSize: 20, fontWeight: 800,
                        '&:disabled': { opacity: 0.4, cursor: 'default' },
                      }}
                    >
                      {b.t}
                    </Box>
                  ) : (
                    // eslint-disable-next-line react/no-array-index-key
                    <Box
                      key={`count-${i}`}
                      component="span"
                      sx={{ minWidth: 16, textAlign: 'center', fontFamily: UI, fontSize: 18, fontWeight: 800 }}
                    >
                      {people}
                    </Box>
                  )))}
                </Box>
              </Box>

              <Box component="span" sx={{ fontFamily: UI, fontSize: 14, color: 'var(--tl-muted-2)' }}>
                {people > 1
                  ? `One visit for ${people} people at the same address.`
                  : 'Add family members at the same address in one visit.'}
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, fontFamily: UI, fontSize: 15 }}>
                {[
                  { l: 'Home collection', v: 'Free', teal: true },
                  { l: 'Doctor review', v: 'Included' },
                  { l: 'Free doctor call', v: 'If anything is flagged' },
                ].map((r) => (
                  <Box key={r.l} component="span" sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
                    <Box component="span" sx={{ color: 'var(--tl-muted-2)' }}>{r.l}</Box>
                    <Box
                      component="span"
                      sx={{ fontWeight: 800, textAlign: 'right', color: r.teal ? 'var(--tl-teal-d)' : 'inherit' }}
                    >
                      {r.v}
                    </Box>
                  </Box>
                ))}
              </Box>

              <Box
                component="button"
                type="button"
                onClick={handleCart}
                sx={{
                  border: 0, cursor: 'pointer', borderRadius: '16px', p: '17px',
                  fontFamily: UI, fontSize: 17, fontWeight: 800,
                  transition: `background-color .25s ${EASE}`,
                  bgcolor: isInCart ? 'var(--tl-teal)' : 'var(--tl-mint)',
                  color: isInCart ? '#FFFFFF' : 'var(--tl-g9)',
                }}
              >
                {isInCart ? 'Added to your visit ✓' : 'Add to visit'}
              </Box>

              <Box
                component={Link}
                to={ROUTES.BOOKING}
                onClick={() => { if (!isInCart) handleCart(); }}
                sx={{
                  display: 'inline-flex', justifyContent: 'center', alignItems: 'center',
                  borderRadius: '16px', p: '17px', textAlign: 'center',
                  bgcolor: 'var(--tl-teal)', color: '#FFFFFF',
                  fontFamily: UI, fontSize: 17, fontWeight: 800, textDecoration: 'none',
                  '&:hover': { bgcolor: 'var(--tl-teal-d)', color: '#FFFFFF' },
                }}
              >
                Book this package
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: '14px 16px', borderRadius: '16px', bgcolor: 'var(--tl-mint)' }}>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 14, fontWeight: 800, color: 'var(--tl-g9)' }}>
                  Same-day report, guaranteed*
                </Box>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 12, lineHeight: 1.5, color: 'var(--tl-muted-2)' }}>
                  *Conditions apply. For most routine tests when your sample is collected
                  before 12 noon. Samples collected later are reported the next morning.
                  Specialty tests take longer.
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1.75, borderTop: '1px solid #EEF3F1' }}>
                <Box
                  component="span"
                  sx={{
                    fontFamily: UI, fontSize: 12, fontWeight: 800, letterSpacing: '.08em',
                    textTransform: 'uppercase', color: 'var(--tl-muted-2)',
                  }}
                >
                  More ways to book
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 0.75 }}>
                  {[
                    { t: 'Call us', href: `tel:${String(phone).replace(/\D/g, '')}` },
                    { t: 'Get a callback', to: ROUTES.SUPPORT },
                    { t: 'WhatsApp', href: `https://wa.me/${whatsapp}`, ext: true },
                  ].map((a) => (
                    <Box
                      key={a.t}
                      {...(a.to ? { component: Link, to: a.to } : { component: 'a', href: a.href })}
                      {...(a.ext ? { target: '_blank', rel: 'noopener noreferrer' } : null)}
                      sx={{
                        p: '12px 6px', borderRadius: '14px', textAlign: 'center',
                        bgcolor: 'var(--tl-mint)', color: 'var(--tl-g9)',
                        fontFamily: UI, fontSize: 13, fontWeight: 800, textDecoration: 'none',
                        '&:hover': { bgcolor: 'var(--tl-mint-2)', color: 'var(--tl-g9)' },
                      }}
                    >
                      {a.t}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PackageDetail;
